import {
    classNames,
    EmailKeyboardLayout,
    Keyboard,
    layout,
    PushButton,
    TextInput,
    typography,
    useTypeRef,
    View,
    ViewContent,
    ViewFooter,
    ViewFooterItem,
    ViewHeader,
    ViewHeaderItem
} from '@variocube/cube-ui';
import * as React from "react";
import {useCallback, useMemo, useRef, useState} from "react";
import {stylesheet} from 'typestyle';
import {useLocalization} from "../../i18n";


interface PackageReleaseCodeView {
    onNext: (packageReleaseCode: string) => void;
    onCancel: () => void;
}

export function PackageReleaseCodeView({ onNext, onCancel }: PackageReleaseCodeView) {
    const {t} = useLocalization();
    const [nameType, setNameType] = useTypeRef();
    const packageReleaseCodeRef = useRef<HTMLInputElement>();
    const [packageReleaseCode, setPackageReleaseCode] = useState<string>("");
    const [packageReleaseCodeWasTyped, setPackageReleaseCodeWasTyped] = useState<boolean>();
    const [confirm, setConfirm] = useState<boolean>();

    const handleConfirm = useCallback(() => {
        setConfirm(true);
    }, [setConfirm]);

    const handleNextClick = useCallback(() => {
        onNext(packageReleaseCode);
    }, [packageReleaseCode, onNext]);

    const handlePackageReleaseCodeChanged = useCallback((code: string) => {
        setPackageReleaseCode(code);
        setPackageReleaseCodeWasTyped(true);
    }, []);

    const packageReleaseCodeValidValid = useMemo<boolean>(() => {
        return packageReleaseCode.length >= 1;

    }, [packageReleaseCode]);

    const keyboardLayout = EmailKeyboardLayout;

    return (
        <View>
            <ViewHeader>
                <ViewHeaderItem className={layout.paddingMedium}>
                    <div className={typography.title}>
                        {t("PackageReleaseCodeTitle")}
                    </div>
                    <div className={typography.description}>
                        {t("PackageReleaseCodeDescription")}
                    </div>
                </ViewHeaderItem>
            </ViewHeader>
            <ViewContent className={layout.paddingMedium}>
                <div className={layout.horizontal}>
                    <div className={styles.field}>
                        <div className={styles.label}>
                            {t("PackageReleaseCodeTitle")}
                        </div>
                        <TextInput
                            value={packageReleaseCode}
                            error={packageReleaseCodeWasTyped && !packageReleaseCodeValidValid}
                            onChange={handlePackageReleaseCodeChanged}
                            inputRef={packageReleaseCodeRef}
                            typeRef={setNameType}
                            disabled={confirm}
                            outline
                            backspace={true}
                        />
                    </div>
                    {!confirm && (
                        <div className={styles.submitField}>
                            <PushButton primary className={styles.submit} disabled={!packageReleaseCodeValidValid} onClick={handleConfirm}>
                                {t("Confirm")}
                            </PushButton>
                            <PushButton className={styles.cancel} onClick={onCancel}>
                                {t("Cancel")}
                            </PushButton>
                        </div>
                    )}
                </div>
            </ViewContent>
            <ViewFooter className={classNames(layout.spaceBetween, layout.paddingMedium)}>
                {!confirm &&
                    <ViewFooterItem fill className={styles.keyboardContainer}>
                        <Keyboard onType={nameType} layout={keyboardLayout}/>
                    </ViewFooterItem>
                }
                {confirm && (
                    <>
                        <ViewFooterItem>
                            <PushButton onClick={onCancel}>
                                {t("Cancel")}
                            </PushButton>
                        </ViewFooterItem>
                        <ViewFooterItem>
                            <PushButton onClick={handleNextClick} primary disabled={!packageReleaseCodeValidValid}>
                                {t("Next")}
                            </PushButton>
                        </ViewFooterItem>
                    </>
                )}

            </ViewFooter>
        </View>
    );
}

const styles = stylesheet({
    canvas: {
        width: "100%",
        height: "100%",
        border: "1px solid #666"
    },
    label: {
        paddingLeft: "1.5em"
    },
    field: {
        padding: "0.75rem"
    },
    submitField: {
        padding: "0.75rem",
        display: "flex",
        alignItems: "end"
    },
    submit: {
        height: "3em"
    },
    cancel: {
        height: "3em",
        marginLeft: "1.2em",
    },
    keyboardContainer: {
        padding: "0.25rem"
    },
});